import { createPortal } from "react-dom";
import { Icon } from "react-migration/components/Icon";
import { DropdownMenu, DropdownMenuSize, useDropdownMenuContext } from "react-migration/components/DropdownMenu";

import Feature from "src/js/stores/user/Feature";
import { Intercom } from "src/js/util/intercom";
import { ENVIRONMENT } from "src/js/util/environment";
import { refreshUser } from "src/js/util/user";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useAccountQuery } from "react-migration/domains/dashboard/topup/hooks/useAccountQuery";
import { userStore } from "src/js/stores/user/store";
import hasFeature from "src/js/stores/user/actions/hasFeature";

import { formatToCurrency } from "react-migration/lib/util/numberFormat";
import { logEvent } from "react-migration/lib/util/logEvent";

import { BalanceUKIcon, BalanceUSIcon, BillingIcon, ChatIcon, ContactIcon, LogoutIcon, PersonIcon } from "../Icons";
import { setShowTopupModal, setDebugModal, openAddOnsStoreModal } from "src/js/stores/navigation/actions";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import { Avatar, AvatarSize } from "react-migration/components/Avatar";
import { ReactPortal } from "react";
import { UserColorMenu } from "./UserColorMenu";
import { handleUpgrade, shouldShowUpgrade } from "react-migration/components/UpgradeButton";

export const UserMenu = () => {
  const { t } = useTranslation();
  const { user } = useSnapshot(userStore);
  const { data: accountData } = useAccountQuery();
  const isUsUser = hasFeature(Feature.usAccess);
  const hasHometrackV2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);
  const { closeMenu: closeColorMenu } = useDropdownMenuContext();

  const openTopupModal = () => {
    setShowTopupModal(true);
    logEvent("Top Up Started", { location: "modal" });
  };

  const openDebugModal = () => {
    setDebugModal(true);
    logEvent("Open Debug Modal");
  };

  const openBilling = () => {
    logEvent("Anchor Link Selected", { link: "customer-portal" });
    window.location.replace(`${ENVIRONMENT.ACCOUNTS_SERVICE_PRIVATE_API_URL}/customer-portal`);
  };

  return (
    <DropdownMenu.Provider size={DropdownMenuSize.AUTO}>
      <DropdownMenu.Trigger data-testid="usermenu-trigger-element" align={DropdownMenu.TriggerAlignment["BOTTOM"]}>
        <Avatar
          firstName={user?.firstname ?? ""}
          lastName={user?.surname ?? ""}
          size={AvatarSize.MD}
          className="relative"
        />
      </DropdownMenu.Trigger>
      {
        createPortal(
          <DropdownMenu.Content className="min-w-[242px]">
            <DropdownMenu.ItemText>
              <div data-testid="your-account" className="flex flex-row items-center flex-1">
                <DropdownMenu.Provider onMenuClosed={() => closeColorMenu()}>
                  <DropdownMenu.Trigger align={DropdownMenu.TriggerAlignment["BOTTOM"]}>
                    <Avatar
                      firstName={user?.firstname ?? ""}
                      lastName={user?.surname ?? ""}
                      size={AvatarSize.MD}
                      className="mr-2"
                    />
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content className="max-w-[160px]">
                    <UserColorMenu />
                  </DropdownMenu.Content>
                </DropdownMenu.Provider>
                <div className="flex grow">
                  <div className="flex flex-col">
                    <span className="text-sm">{`${user.firstname} ${user.surname}`}</span>
                    <span className="text-xs capitalize text-neutral-400">{`${user.plan_name} Plan`}</span>
                  </div>
                </div>
                <div className="flex-none">
                  <PersonIcon />
                </div>
              </div>
            </DropdownMenu.ItemText>
            {shouldShowUpgrade("UserMenu") && (
              <DropdownMenu.Item
                onClick={() => handleUpgrade({ source: "UserMenu" })}
                data-testid="logout-element"
                className="flex items-center gap-2"
              >
                <Icon icon="icon-lt-arrow-up-circle-line" extraClasses="opacity-60" />
                <span className="text-sm">{t("nav.nav.upgrade")}</span>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item
              onClick={() => openBilling()}
              data-testid="logout-element"
              className="flex items-center gap-2"
            >
              <BillingIcon />
              <span className="text-sm">{t("nav.nav.billing")}</span>
            </DropdownMenu.Item>
            {!hasFeature(Feature.usAccess) && (
              <DropdownMenu.Item
                data-testid="logout-element"
                className="flex items-center gap-2"
                disabled={!hasFeature(Feature.documentPurchase)}
                onClick={() => openTopupModal()}
              >
                {hasFeature(Feature.usAccess) ? <BalanceUSIcon /> : <BalanceUKIcon />}
                <span className="text-sm">{t("nav.nav.balance")}</span>
                <span className="text-sm">
                  {(accountData?.accountWithViews?.land_reg_credit_pounds || 0) < 0 && "-"}
                  {`${formatToCurrency({
                    amount: accountData?.accountWithViews.land_reg_credit_pounds || 0,
                    currency: hasFeature(Feature.usAccess) ? "USD" : "GBP",
                  })}`}
                </span>
              </DropdownMenu.Item>
            )}
            {!isUsUser && hasHometrackV2 && (
              <DropdownMenu.Item
                onClick={() =>
                  openAddOnsStoreModal({
                    logClickSource: "user-menu",
                  })
                }
                data-testid="addons-store"
                className="flex items-center gap-2"
              >
                <Icon icon="icon-lt-box-3-line" extraClasses="opacity-60" />
                <span className="text-sm">{t("nav.nav.addons")}</span>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Separator className="bg-neutral-200 mx-3" />
            <DropdownMenu.ItemText>
              <div data-testid="your-account" className="flex flex-row items-center flex-1">
                <div className="flex grow">
                  <div className="flex flex-col">
                    <span className="text-sm">{t("nav.nav.support")}</span>
                    <span className="text-sm capitalize text-neutral-400">
                      {hasFeature(Feature.usAccess) ? "calum@land.tech" : "0203 086 7855"}
                    </span>
                  </div>
                </div>
                <div className="flex-none">
                  <ContactIcon />
                </div>
              </div>
            </DropdownMenu.ItemText>
            <DropdownMenu.Item
              data-testid="logout-element"
              className="flex items-center gap-2"
              onClick={() => Intercom("show")}
            >
              <ChatIcon />
              <span className="text-sm">{t("nav.nav.chat")}</span>
            </DropdownMenu.Item>
            {(ENVIRONMENT.APP_ENV === "local" || hasBetaFeature(Feature.debugMenu)) && (
              <DropdownMenu.Item
                data-testid="clear-cache"
                className="flex items-center gap-2"
                onClick={() => openDebugModal()}
              >
                <Icon icon="icon-lt-alert-line" extraClasses="opacity-60" />
                <span className="text-sm">{t("nav.nav.debug")}</span>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Separator />
            <DropdownMenu.ItemLink
              href={ENVIRONMENT.LOGOUT_URL}
              data-testid="logout-element"
              className="flex items-center gap-2"
            >
              <LogoutIcon />
              <span className="text-sm">{t("nav.nav.logout")}</span>
            </DropdownMenu.ItemLink>
            {ENVIRONMENT.APP_ENV === "local" && (
              <DropdownMenu.ItemLink
                data-testid="refresh-user"
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => refreshUser()}
              >
                <PersonIcon />
                <span className="text-sm">Refresh User</span>
              </DropdownMenu.ItemLink>
            )}
          </DropdownMenu.Content>,
          document.body
        ) as ReactPortal
      }
    </DropdownMenu.Provider>
  );
};
